/**
 * Cart-line price/quantity helpers — one source of truth for Cart, CartSidebar
 * and Checkout. Backend cart endpoints sometimes return inconsistent field
 * names (`productPrice` vs `price` vs `itemPrice`); these helpers absorb that
 * variability so the UI never silently shows a $0 line.
 */

/** Return the unit price of a cart line, regardless of which field the
 *  backend used. Returns 0 only as a last resort. */
export function getItemPrice(item) {
  if (!item) return 0;
  const candidates = [item.productPrice, item.price, item.itemPrice];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

/** Quantity, defaulting to 0 if missing/invalid. */
export function getItemQuantity(item) {
  const n = Number(item?.quantity);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Line subtotal (unit price × quantity). */
export function getLineSubtotal(item) {
  return getItemPrice(item) * getItemQuantity(item);
}

/** Sum of all line subtotals. Items with no resolvable price contribute 0
 *  but a dev-mode warning is emitted so the issue surfaces during testing. */
export function calculateCartTotal(cartItems = []) {
  let total = 0;
  for (const item of cartItems) {
    const sub = getLineSubtotal(item);
    if (process.env.NODE_ENV !== 'production' && sub === 0) {
      // eslint-disable-next-line no-console
      console.warn(
        '[cartPrice] Line with $0 subtotal — backend may be missing productPrice:',
        item
      );
    }
    total += sub;
  }
  return total;
}

/** Number of distinct cart lines (e.g. "2 productos" = 2 different items). */
export function getUniqueProductCount(cartItems = []) {
  return Array.isArray(cartItems) ? cartItems.length : 0;
}

/** Total units across all lines (sum of quantities). */
export function getTotalUnits(cartItems = []) {
  if (!Array.isArray(cartItems)) return 0;
  return cartItems.reduce((sum, it) => sum + getItemQuantity(it), 0);
}

/** Format the cart counter label in rioplatense Spanish.
 *  Examples:
 *    1 line × qty 1  → "1 producto"
 *    2 lines × qty 1 → "2 productos"
 *    1 line × qty 3  → "1 producto · 3 unidades"
 *    2 lines × qty 2 → "2 productos · 4 unidades"
 */
export function formatCartCounter(cartItems = []) {
  const lines = getUniqueProductCount(cartItems);
  const units = getTotalUnits(cartItems);
  if (lines === 0) return '';
  const productsLabel = `${lines} ${lines === 1 ? 'producto' : 'productos'}`;
  if (units > lines) {
    return `${productsLabel} · ${units} unidades`;
  }
  return productsLabel;
}

/** Stable React key for a cart line. Prefers the DB id (always unique);
 *  falls back to productId+variantId+index to avoid render skipping when the
 *  backend hasn't assigned an id yet (e.g. optimistic add). */
export function getCartLineKey(item, index) {
  if (item?.id) return String(item.id);
  const variantId = item?.variantId || item?.variant?.id || 'novariant';
  return `${item?.productId || 'noprod'}-${variantId}-${index}`;
}
